import React from 'react';
import { 
  Server, 
  Video, 
  UploadCloud, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Eye, 
  ArrowDownRight, 
  ArrowUpRight,
  Clock,
  Layers
} from 'lucide-react';

export const StreamStatusCards = ({ mediaMtxVitals, uploaderVitals, workerVitals }) => {
  const { 
    readersCount = 0, 
    bytesReceivedMbps = 0, 
    bytesSentMbps = 0, 
    streamReady = false 
  } = mediaMtxVitals || {};

  const { 
    segmentsUploaded = 0, 
    uploadFailures = 0, 
    secondsSinceLastUpload = 0, 
    uploaderHealthy = false 
  } = uploaderVitals || {};

  const { 
    activeViewers = 0, 
    workerHealthy = null, 
    reason = null 
  } = workerVitals || {};

  const isWorkerHealthy = workerHealthy === true;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
      
      {/* 1. Worker Health Card */}
      <div className="glass-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: isWorkerHealthy ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isWorkerHealthy ? 'var(--primary-emerald)' : 'var(--status-danger)'
            }}>
              <Server size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>Worker Vitals</h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Stream Transcoder & Session Node</p>
            </div>
          </div>

          <span className={`badge-status ${isWorkerHealthy ? 'healthy' : 'unhealthy'}`}>
            {isWorkerHealthy ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {isWorkerHealthy ? 'Healthy' : 'NOT READY'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Eye size={12} color="var(--primary-emerald)" /> Active Viewers
            </span>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#34d399', marginTop: '4px' }}>
              {activeViewers}
            </div>
          </div>

          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Worker Status</span>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: isWorkerHealthy ? 'var(--text-main)' : 'var(--status-danger)', marginTop: '6px' }}>
              {isWorkerHealthy ? 'Operational' : 'NOT READY'}
            </div>
          </div>
        </div>

        {reason && (
          <div style={{ 
            marginTop: '12px', 
            padding: '8px 12px', 
            background: 'rgba(239, 68, 68, 0.15)', 
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#f87171',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <AlertTriangle size={14} />
            <span>Reason: {reason}</span>
          </div>
        )}
      </div>

      {/* 2. MediaMTX Stream Vitals Card */}
      <div className="glass-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'rgba(52, 211, 153, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-mint)'
            }}>
              <Video size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>MediaMTX Vitals</h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Live RTSP / HLS Media Engine</p>
            </div>
          </div>

          <span className={`badge-status ${streamReady ? 'healthy' : 'unhealthy'}`}>
            {streamReady ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {streamReady ? 'Stream Ready' : 'Not Ready'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Readers</span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ffffff', marginTop: '2px' }}>
              {readersCount}
            </div>
          </div>

          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
              <ArrowDownRight size={10} color="var(--primary-emerald)" /> Rx Mbps
            </span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--accent-mint)', marginTop: '2px' }}>
              {bytesReceivedMbps}
            </div>
          </div>

          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
              <ArrowUpRight size={10} color="var(--accent-light)" /> Tx Mbps
            </span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ffffff', marginTop: '2px' }}>
              {bytesSentMbps}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Uploader Vitals Card */}
      <div className="glass-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'rgba(16, 185, 129, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary-emerald)'
            }}>
              <UploadCloud size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>Uploader Vitals</h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Segment Upload Pipeline</p>
            </div>
          </div>

          <span className={`badge-status ${uploaderHealthy ? 'healthy' : 'unhealthy'}`}>
            {uploaderHealthy ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {uploaderHealthy ? 'Uploader OK' : 'Failure'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
              <Layers size={10} color="var(--primary-emerald)" /> Segments
            </span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ffffff', marginTop: '2px' }}>
              {segmentsUploaded}
            </div>
          </div>

          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Failures</span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: uploadFailures > 0 ? 'var(--status-danger)' : 'var(--accent-mint)', marginTop: '2px' }}>
              {uploadFailures}
            </div>
          </div>

          <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
              <Clock size={10} color="var(--accent-mint)" /> Sec Since
            </span>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: secondsSinceLastUpload > 10 ? 'var(--status-warning)' : '#ffffff', marginTop: '2px' }}>
              {secondsSinceLastUpload}s
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
